/**
 * Tenant-scoped wrapper around window.localStorage.
 *
 * Every key written/read through `tenantStorage` is automatically prefixed
 * with `divi:<tenantId>:` so a user who visits tenant-A then later visits
 * tenant-B on the same device does NOT see tenant-A's cart, affiliate
 * code, checkout form data, etc.
 *
 * Usage:
 *
 *   import { tenantStorage } from '@/utils/storage';
 *   tenantStorage.setItem('cart', JSON.stringify(items));
 *   const raw = tenantStorage.getItem('cart');
 *
 * One bootstrap step is required: `TenantContext` must call
 * `tenantStorage.setTenant(tenantId)` as soon as the tenant resolves so
 * subsequent reads/writes hit the correct namespace. Until that call,
 * values are written under `divi:_pending:<name>`; the next call to
 * `setTenant` migrates them into the tenant's namespace.
 *
 * Direct `window.localStorage` access is still allowed for non-tenant
 * concerns (debug flags, ESLint preferences, etc.). Anything user-data-
 * shaped should go through this helper.
 */

const NAMESPACE = 'divi';
const PENDING_KEY = `${NAMESPACE}:_pending`;

class TenantStorage {
  private tenantKey: string | null = null;

  /**
   * Bind storage to a tenant. Pass `null` to reset to pending-scope (e.g.
   * on full logout / tenant teardown). When binding to a new tenant, any
   * keys still in the pending namespace are migrated under the new prefix
   * so users who acted before the tenant resolved (e.g. clicked an
   * affiliate link) keep their state.
   */
  setTenant(tenantId: string | null): void {
    const next = tenantId ? `${NAMESPACE}:${tenantId}` : null;
    if (next === this.tenantKey) return;

    if (next) {
      this.migratePendingTo(next);
    }
    this.tenantKey = next;
  }

  /** Returns the bound tenant id, or null if storage is in pending scope. */
  getTenantId(): string | null {
    if (!this.tenantKey) return null;
    return this.tenantKey.slice(`${NAMESPACE}:`.length);
  }

  getItem(name: string): string | null {
    try {
      return window.localStorage.getItem(this.scopedKey(name));
    } catch {
      return null;
    }
  }

  setItem(name: string, value: string): void {
    try {
      window.localStorage.setItem(this.scopedKey(name), value);
    } catch {
      // Quota / private-mode: silently drop. Caller code already assumed
      // localStorage is best-effort.
    }
  }

  removeItem(name: string): void {
    try {
      window.localStorage.removeItem(this.scopedKey(name));
    } catch {
      // ignore
    }
  }

  /**
   * Wipe every key under the current tenant scope (or pending scope if no
   * tenant is bound). Other tenants' data on this device is untouched.
   */
  clear(): void {
    try {
      const prefix = `${this.prefix()}:`;
      const toRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k && k.startsWith(prefix)) toRemove.push(k);
      }
      toRemove.forEach((k) => window.localStorage.removeItem(k));
    } catch {
      // ignore
    }
  }

  /** Escape hatch for callers that need the fully-qualified key (debug only). */
  rawKey(name: string): string {
    return this.scopedKey(name);
  }

  private prefix(): string {
    return this.tenantKey ?? PENDING_KEY;
  }

  private scopedKey(name: string): string {
    return `${this.prefix()}:${name}`;
  }

  private migratePendingTo(newPrefix: string): void {
    try {
      const pendingPrefix = `${PENDING_KEY}:`;
      const moves: Array<{ oldKey: string; newKey: string; value: string }> =
        [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (!k || !k.startsWith(pendingPrefix)) continue;
        const value = window.localStorage.getItem(k);
        if (value === null) continue;
        const suffix = k.slice(pendingPrefix.length);
        moves.push({ oldKey: k, newKey: `${newPrefix}:${suffix}`, value });
      }
      for (const m of moves) {
        // Do not overwrite a value already present in the destination scope.
        if (window.localStorage.getItem(m.newKey) === null) {
          window.localStorage.setItem(m.newKey, m.value);
        }
        window.localStorage.removeItem(m.oldKey);
      }
    } catch {
      // ignore
    }
  }
}

export const tenantStorage = new TenantStorage();
